import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../services/api';

interface MaintenanceState {
  enabled: boolean;
  message?: string;
  exclude_paths?: string[];
  exclude_user_ids?: number[];
}

interface AnnouncementBannerState {
  enabled: boolean;
  show_for_guests: boolean;
  text: string;
  url: string;
  link_text: string;
}

interface SearchMessage {
  enabled: boolean;
  title: string;
  text: string;
  severity: string;
}

interface SiteNoticeState {
  enabled: boolean;
  text: string;
  severity: string;
  url?: string;
  notice_id?: string;
}

interface AiCompanionState {
  enabled: boolean;
}

interface AppState {
  maintenance: MaintenanceState;
  announcement_banner: AnnouncementBannerState | null;
  search_messages: Record<string, SearchMessage[]>;
  site_notice: SiteNoticeState | null;
  ai_companion: AiCompanionState;
  loaded: boolean;
}

const defaultState: AppState = {
  maintenance: { enabled: false },
  announcement_banner: null,
  search_messages: { game: [], anime: [], movie: [] },
  site_notice: null,
  ai_companion: { enabled: false },
  loaded: false,
};

const AppStateContext = createContext<AppState>(defaultState);

export const useAppState = () => useContext(AppStateContext);

export const AppStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>(defaultState);

  const fetchAppState = useCallback(() => {
    api.get('/app-state/').then(res => {
      setState({ ...defaultState, ...res.data, loaded: true });
    }).catch(() => {
      setState(prev => ({ ...prev, loaded: true }));
    });
  }, []);

  useEffect(() => {
    fetchAppState();
  }, [fetchAppState]);

  // タブがアクティブに戻ったときに再取得（メンテ切替を反映）
  // + 5分ごとの定期再取得
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchAppState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const interval = setInterval(fetchAppState, 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [fetchAppState]);

  return (
    <AppStateContext.Provider value={state}>
      {children}
    </AppStateContext.Provider>
  );
};
